import { useState } from 'react';
import { ArrowRight, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { PublicGeniePersona } from '@/types/discovery-catalog';
import type { BaseCapability } from '@/types/genie';
import { PersonaBrand } from '@/data/genies/persona-brands';

interface PostConversationCardProps {
  persona: PublicGeniePersona;
  brand: PersonaBrand | undefined;
  primaryColor: string;
  logoPath: string | null;
  capability: BaseCapability | undefined;
  isMobile: boolean;
  onCloneClick: () => void;
  onTryAgain: () => void;
}

export const PostConversationCard = ({
  persona,
  brand,
  primaryColor,
  logoPath,
  capability,
  isMobile,
  onCloneClick,
  onTryAgain,
}: PostConversationCardProps) => {
  const [logoError, setLogoError] = useState(false);

  const benefits = buildBenefits(persona, capability, primaryColor);

  return (
    <div
      style={{
        maxWidth: isMobile ? '100%' : '480px',
        width: '100%',
        background: 'white',
        borderRadius: isMobile ? '20px' : '24px',
        boxShadow: '0 8px 32px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.04)',
        overflow: 'hidden',
        borderTop: `3px solid ${primaryColor}`,
        animation: 'fadeInUp 0.4s ease-out',
      }}
    >
      {/* Brand Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '14px',
          padding: isMobile ? '16px 20px' : '20px 28px',
          background: `linear-gradient(135deg, ${primaryColor}0A 0%, ${primaryColor}14 100%)`,
          borderBottom: `1px solid ${primaryColor}10`,
        }}
      >
        {/* Small brand logo */}
        <div
          style={{
            width: isMobile ? 48 : 56,
            height: isMobile ? 48 : 56,
            borderRadius: '50%',
            background: `${primaryColor}18`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: `0 2px 8px ${primaryColor}20`,
          }}
        >
          {logoPath && !logoError ? (
            <img
              src={logoPath}
              alt={brand?.brandName || persona.name}
              style={{
                width: '60%',
                height: '60%',
                objectFit: 'contain',
              }}
              onError={() => setLogoError(true)}
            />
          ) : (
            <div
              style={{
                width: '50%',
                height: '50%',
                borderRadius: '50%',
                background: primaryColor,
              }}
            />
          )}
        </div>
        <div style={{ minWidth: 0 }}>
          <p
            style={{
              fontSize: isMobile ? '0.95rem' : '1.05rem',
              fontWeight: 700,
              color: '#1F2937',
              margin: 0,
              lineHeight: 1.3,
            }}
          >
            {brand?.brandName || persona.name}
          </p>
          {brand?.tagline && (
            <p
              style={{
                fontSize: '0.8rem',
                color: '#6B7280',
                margin: '2px 0 0',
                lineHeight: 1.3,
              }}
            >
              {brand.tagline}
            </p>
          )}
        </div>
      </div>

      {/* Card Body */}
      <div style={{ padding: isMobile ? '20px 20px 24px' : '28px 28px 32px' }}>
        {/* Emotional Hook */}
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <h2
            style={{
              fontSize: isMobile ? '1.3rem' : '1.5rem',
              fontWeight: 800,
              color: '#1F2937',
              margin: '0 0 8px 0',
              letterSpacing: '-0.02em',
            }}
          >
            Like what you heard?
          </h2>
          <p
            style={{
              fontSize: isMobile ? '0.85rem' : '0.9rem',
              color: '#6B7280',
              margin: 0,
              lineHeight: 1.5,
              maxWidth: '320px',
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
          >
            {persona.name} can handle calls like this for your business — ready in under a minute.
          </p>
        </div>

        {/* Benefits Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
            gap: isMobile ? '8px' : '10px',
            marginBottom: '24px',
          }}
        >
          {benefits.map((b) => (
            <div
              key={b.label}
              style={{
                padding: isMobile ? '10px 12px' : '12px 14px',
                borderRadius: '12px',
                background: `${primaryColor}06`,
                borderLeft: `3px solid ${primaryColor}`,
              }}
            >
              <p
                style={{
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  color: '#374151',
                  margin: 0,
                  lineHeight: 1.3,
                }}
              >
                {b.label}
              </p>
              <p
                style={{
                  fontSize: '0.7rem',
                  color: '#9CA3AF',
                  margin: '2px 0 0',
                  lineHeight: 1.3,
                }}
              >
                {b.description}
              </p>
            </div>
          ))}
        </div>

        {/* Trust Line */}
        <p
          style={{
            textAlign: 'center',
            fontSize: '0.72rem',
            color: '#9CA3AF',
            margin: '0 0 20px 0',
            letterSpacing: '0.02em',
          }}
        >
          No credit card required · Ready in 60 seconds · Customize everything
        </p>

        {/* Primary CTA */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            onClick={onCloneClick}
            style={{
              background: primaryColor,
              color: 'white',
              fontWeight: 600,
              borderRadius: '24px',
              padding: isMobile ? '12px 28px' : '14px 36px',
              fontSize: isMobile ? '0.9rem' : '1rem',
              boxShadow: `0 4px 14px ${primaryColor}30`,
              width: isMobile ? '100%' : 'auto',
              minWidth: isMobile ? undefined : '280px',
              transition: 'all 0.2s ease',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = `0 6px 20px ${primaryColor}40`;
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = `0 4px 14px ${primaryColor}30`;
            }}
          >
            Create My Genie
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        {/* Secondary Action */}
        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <button
            onClick={onTryAgain}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.82rem',
              color: '#9CA3AF',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 8px',
              borderRadius: '8px',
              transition: 'color 0.2s ease',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.color = '#6B7280';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.color = '#9CA3AF';
            }}
          >
            <RefreshCw style={{ width: 13, height: 13 }} />
            or try the demo again
          </button>
        </div>
      </div>
    </div>
  );
};

/** Build benefits list from persona data */
function buildBenefits(
  persona: PublicGeniePersona,
  capability: BaseCapability | undefined,
  _primaryColor: string,
) {
  const benefits: Array<{ label: string; description: string }> = [];

  if (capability) {
    benefits.push({
      label: capability.name,
      description: capability.description.length > 60
        ? capability.description.substring(0, 57) + '...'
        : capability.description,
    });
  }

  if (persona.config.collectFields && persona.config.collectFields.length > 0) {
    benefits.push({
      label: 'Lead Capture',
      description: 'Collects contact details during the call',
    });
  }

  if (persona.config.afterHours) {
    benefits.push({
      label: '24/7 Available',
      description: 'Handles calls even after business hours',
    });
  }

  if (persona.config.escalationRules && persona.config.escalationRules.length > 0) {
    benefits.push({
      label: 'Smart Escalation',
      description: 'Routes urgent calls to the right person',
    });
  }

  benefits.push({
    label: 'Fully Customizable',
    description: 'Adjust voice, personality, and responses',
  });

  return benefits;
}
